import { useApiClient } from '@/composables/useApiClient'
import { addEndpointPrefix } from '@/utils/apiAdapter'
import { querySerializer } from '@/utils/common'
import { adaptRequiredPaymentParams } from '@/service/payment'

const getDefaultState = () => {
  return {
    currency: {},
    gatewayInfo: {},
    binCode: null,
    usingFunCoin: false,
    selectedPromoCode: {},
    selectedCreditCard: {},
    paymentMethod: '',
    paymentAmount: {
      currency_code: '',
      promo_discount: 0,
      fun_coin_discount: '0',
      remaining_fun_coin: 0,
      txn_amount: 0,
    },
    totalAmount: 0,
    creditCardList: [],
    limitNotice: '',
  }
}

export const state = () => getDefaultState()

export const getters = {
  currency: state => state.currency,
  gatewayInfo: state => state.gatewayInfo,
  binCode: state => state.binCode,
  usingFunCoin: state => state.usingFunCoin,
  selectedPromoCode: state => state.selectedPromoCode,
  selectedCreditCard: state => state.selectedCreditCard,
  paymentMethod: state => state.paymentMethod,
  paymentAmount: state => state.paymentAmount,
  totalAmount: state => state.totalAmount,
  creditCardList: state => state.creditCardList,
  limitNotice: state => state.limitNotice,
}

export const actions = {
  resetPaymentState({ commit }) {
    commit('RESET_PAYMENT_STATE')
  },
  setSelectedPromoCode({ commit }, promoCode) {
    commit('SET_SELECTED_PROMO_CODE', promoCode)
  },
  setStateOfUsingFunCoin({ commit }, statement) {
    commit('SET_STATE_OF_USING_FUN_COIN', statement)
  },
  setSelectedCreditCard({ commit }, selectedCreditCard) {
    commit('SET_SELECTED_CREDIT_CARD', selectedCreditCard)
  },
  selectCardOnListLoad({ commit, state }, creditCardList) {
    if (!creditCardList?.length) {
      commit('SET_SELECTED_CREDIT_CARD', null)
      return
    }

    const isSelectedCreditCardValid = creditCardList.some(card => card.id === state.selectedCreditCard?.id && card.usable)

    if (!isSelectedCreditCardValid) {
      /** 第一張卡是上次付款的信用卡 */
      const [lastTimeCard] = creditCardList
      const selectedCreditCardPayload = lastTimeCard.usable ? lastTimeCard : null

      commit('SET_SELECTED_CREDIT_CARD', selectedCreditCardPayload)
    }
  },
  setPaymentMethod({ commit }, paymentMethod) {
    commit('SET_PAYMENT_METHOD', paymentMethod)
  },
  setTotalAmount({ commit }, totalAmount) {
    commit('SET_TOTAL_AMOUNT', totalAmount)
  },
  getBinCodeList({ commit }, { t, orderId }) {
    return new Promise((resolve, reject) => {
      const prefix = addEndpointPrefix(!!t)
      const apiUrl = prefix + '/credit/bin_code'
      const params = {}

      if (t) {
        Object.assign(params, { order_id: orderId, t })
      }

      useApiClient()
        .$get(apiUrl, { params })
        .then(res => {
          if (res.code === 0) {
            const binCodeList = res.data || []
            commit('SET_BIN_CODE', binCodeList)
          }
          resolve(res)
        })
        .catch(error => {
          reject(error)
        })
    })
  },
  getCreditCardList({ commit, dispatch }, payload) {
    return new Promise((resolve, reject) => {
      const apiUrl = '/payment'
      useApiClient()
        .$get(`${apiUrl}?${querySerializer(payload)}`)
        .then(res => {
          if (res.code === 0) {
            dispatch('selectCardOnListLoad', res.data)
            commit('SET_CREDIT_CARD_LIST', res.data)
            commit('SET_LIMIT_NOTICE', res.limit_notice)
          }
          resolve(res)
        })
        .catch(error => {
          reject(error)
        })
    })
  },
  getPaymentAmount({ commit }, { productID, amount, currencyID, useFunCoin, promoCodeID, promoUsage, productAmount, bookingTime, t, orderId }) {
    const params = {
      product_id: productID,
      amount: amount,
      currency_id: currencyID,
      booking_time: bookingTime,
    }
    const optionsParams = {
      use_fun_coin: useFunCoin || false,
      code_id: promoCodeID || '',
      promo_usage: promoUsage || '',
      product_amount: productAmount || '',
    }

    if (t) {
      Object.assign(optionsParams, { order_id: orderId, t })
    }

    const prefix = addEndpointPrefix(!!t)
    const apiUrl = prefix + '/payment/amount'

    return useApiClient()
      .$get(apiUrl, { params: { ...params, ...optionsParams } })
      .then(res => {
        if (res.code === 0 || res.code === 215) {
          commit('SET_PAYMENT_AMOUNT', res.data)
        }
        return res
      })
      .catch(error => {
        console.error('getPaymentAmount:error', error)
        return error
      })
  },
  /** token 在 post body 中用 token，在 get 請求中叫做 t */
  payByNone({ commit }, { orderID, promoCodeID, promoUsage, token }) {
    const prefix = addEndpointPrefix(!!token)
    const apiUrl = prefix + '/payment/none'

    const params = {
      orderid: orderID,
      code_id: promoCodeID,
      promo_usage: promoUsage,
      token,
    }
    return useApiClient()
      .$post(apiUrl, params)
      .then(res => {
        return res
      })
      .catch(error => {
        console.error('payByNone:error', error)
        return error
      })
  },
  payByCredit({ commit }, paymentData) {
    const prefix = addEndpointPrefix(!!paymentData?.token)
    const apiUrl = prefix + '/payment/payByCredit'

    const params = Object.assign(
      {
        cardid: paymentData.cardID,
        cvv: paymentData.cvv,
        prime: paymentData.prime,
        code_id: paymentData.promoCodeID || null,
        promo_usage: paymentData.promoUsage,
        token: paymentData?.token,
        cardholder: paymentData.cardholder || null,
      },
      adaptRequiredPaymentParams(paymentData)
    )

    if (paymentData.hasOwnProperty('isNewCard')) {
      Object.assign(params, {
        is_new_card: paymentData.isNewCard,
        bin: paymentData.bin,
        bin_code_id: paymentData.binCodeID,
        currency_id: paymentData.currencyId,
      })
    }

    return useApiClient()
      .$post(apiUrl, params)
      .catch(error => {
        console.error('payByCredit:error', error)
        return error
      })
  },
  payByGrabPay({ commit }, paymentData) {
    const prefix = addEndpointPrefix(!!paymentData?.token)
    const apiUrl = prefix + '/payment/grabpay/init'

    const params = Object.assign(
      {
        promo_usage: paymentData.promoUsage,
        code_id: paymentData.promoCodeID || null,
        token: paymentData?.token,
      },
      adaptRequiredPaymentParams(paymentData)
    )
    Object.keys(params).forEach(key => params[key] === null && delete params[key])

    return useApiClient()
      .$post(apiUrl, params)
      .catch(error => {
        console.error('payByGrabPay:error', error)
        return error
      })
  },
  getOrderStatus({ commit }, { orderId, token }) {
    const prefix = addEndpointPrefix(!!token)
    const apiUrl = prefix + `/order/${orderId}/status`
    return useApiClient()
      .$get(apiUrl, { params: { ...(token ? { t: token } : {}) } })
      .then(res => {
        return res
      })
      .catch(error => {
        console.error('getOrderStatus:error', error)
        return error
      })
  },
}

export const mutations = {
  RESET_PAYMENT_STATE(state) {
    Object.assign(state, getDefaultState())
  },
  SET_SELECTED_PROMO_CODE(state, selectedPromoCode) {
    state.selectedPromoCode = Object.assign({}, selectedPromoCode)
  },
  SET_STATE_OF_USING_FUN_COIN(state, usingFunCoin) {
    state.usingFunCoin = usingFunCoin
  },
  SET_SELECTED_CREDIT_CARD(state, selectedCreditCard) {
    state.selectedCreditCard = Object.assign({}, selectedCreditCard)
  },
  SET_PAYMENT_METHOD(state, paymentMethod) {
    state.paymentMethod = paymentMethod
  },
  SET_CURRENCY(state, currency) {
    state.currency = currency
  },
  SET_GATEWAY_INFO(state, gatewayInfo) {
    state.gatewayInfo = gatewayInfo
  },
  SET_BIN_CODE(state, binCode) {
    state.binCode = binCode
  },
  SET_PAYMENT_AMOUNT(state, paymentAmount) {
    state.paymentAmount = Object.assign({}, paymentAmount)
  },
  SET_TOTAL_AMOUNT(state, totalAmount) {
    state.totalAmount = totalAmount
  },
  SET_CREDIT_CARD_LIST(state, creditCardList) {
    state.creditCardList = [...creditCardList]
  },
  SET_LIMIT_NOTICE(state, limitNotice) {
    state.limitNotice = limitNotice
  },
}
